import { FC, useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Spin, Alert, Switch, Space } from 'antd';
import OffsetForm from '../../components/OffsetForm';
import OffsetList from '../../components/OffsetList';
import { fetchActiveOffsets, fetchAllOffsets } from '../../api/priceOffset';
import type { PriceOffset, PriceOffsetWithPointCount } from '../../api/priceOffset';

const PriceOffsetPage: FC = () => {
  const [loading, setLoading] = useState(true);
  const [offsets, setOffsets] = useState<PriceOffset[]>([]);
  const [offsetsWithCount, setOffsetsWithCount] = useState<PriceOffsetWithPointCount[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedOffset, setSelectedOffset] = useState<{
    productCode: string; 
    offsetValue: number;
    id?: number;
    isUpdate?: boolean;
  } | undefined>();
  const [showHistory, setShowHistory] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (showHistory) {
        const data = await fetchAllOffsets();
        setOffsetsWithCount(data);
      } else {
        const data = await fetchActiveOffsets();
        setOffsets(data);
      }
    } catch (e) {
      console.error('加载价格偏移失败', e);
      setError('加载价格偏移数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [showHistory]);

  useEffect(() => {
    loadData();
  }, [showHistory, loadData]);

  const handleViewOffset = (offset: PriceOffset) => {
    setSelectedOffset({
      productCode: offset.productCode,
      offsetValue: offset.offsetValue,
      id: offset.id,
      isUpdate: true
    });
    setShowHistory(false); // 切换到编辑模式
  };

  const handleFormSuccess = (result?: { 
    created?: PriceOffset; 
    updated?: { id: number; offsetValue: number; productCode: string }; 
  }) => {
    if (result?.created) {
      // 创建偏移成功后，自动进入编辑模式
      setSelectedOffset({
        productCode: result.created.productCode,
        offsetValue: result.created.offsetValue,
        id: result.created.id,
        isUpdate: true
      });
    } else if (result?.updated) {
      // 更新偏移成功后，同步更新 selectedOffset 中的偏移量
      setSelectedOffset({
        productCode: result.updated.productCode,
        offsetValue: result.updated.offsetValue,
        id: result.updated.id,
        isUpdate: true
      });
    }
    loadData();
  };

  const handleFormReset = () => {
    setSelectedOffset(undefined);
  };

  const handleHistoryToggle = (checked: boolean) => {
    setShowHistory(checked);
    if (checked) {
      setSelectedOffset(undefined); // 清除选中的偏移
    }
  };

  return (
    <div className="price-offset-page">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            title="价格偏移设置"
            extra={
              <Space>
                <span>查看历史记录</span>
                <Switch 
                  checked={showHistory}
                  onChange={handleHistoryToggle}
                  disabled={!!selectedOffset}
                />
              </Space>
            }
          >
            {!showHistory && (
              <OffsetForm 
                onSuccess={handleFormSuccess}
                onReset={handleFormReset}
                initialValues={selectedOffset ? {
                  productCode: selectedOffset.productCode,
                  offsetValue: selectedOffset.offsetValue
                } : undefined}
                updateMode={selectedOffset?.isUpdate ? {
                  id: selectedOffset.id!,
                  isUpdate: true
                } : undefined}
              />
            )}
            {showHistory && (
              <Alert 
                type="info" 
                message="历史记录模式" 
                description="在此模式下，您可以查看所有历史记录，管理记录状态和数据。" 
                showIcon
              />
            )}
          </Card>
        </Col>
        <Col span={24}>
          {loading ? (
            <Card>
              <Spin tip="加载价格偏移数据..." />
            </Card>
          ) : error ? (
            <Alert type="error" message={error} />
          ) : (
            <OffsetList 
              offsets={showHistory ? undefined : offsets}
              offsetsWithCount={showHistory ? offsetsWithCount : undefined}
              onRefreshNeeded={loadData}
              onView={handleViewOffset}
              showHistoryMode={showHistory}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PriceOffsetPage; 